'use client';

import { useState, useEffect } from 'react';
import type { EntityCost, CostType } from '@/lib/command-center/types';

interface CostTrackerProps {
  entityId: string;
  entityName?: string;
  workspaceId: string;
  onCostChange?: () => void;
  compact?: boolean;
}

const COST_TYPE_LABELS: Record<CostType, { label: string; color: string; icon: string }> = {
  infrastructure: { label: 'Infrastructure', color: 'text-blue-400', icon: 'M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01' },
  licensing: { label: 'Licensing', color: 'text-purple-400', icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' },
  labor: { label: 'Labor', color: 'text-green-400', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
  support: { label: 'Support', color: 'text-yellow-400', icon: 'M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z' },
  other: { label: 'Other', color: 'text-gray-400', icon: 'M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
};

const PERIOD_LABELS: Record<string, string> = {
  monthly: 'Monthly',
  yearly: 'Yearly',
  one_time: 'One-time',
};

function formatCurrency(amount: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function CostTracker({
  entityId,
  entityName,
  workspaceId,
  onCostChange,
  compact = false,
}: CostTrackerProps) {
  const [costs, setCosts] = useState<EntityCost[]>([]);
  const [summary, setSummary] = useState<{
    monthly_cost: number;
    yearly_cost: number;
    one_time_cost: number;
  }>({ monthly_cost: 0, yearly_cost: 0, one_time_cost: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [adding, setAdding] = useState(false);

  // Form state
  const [costType, setCostType] = useState<CostType>('infrastructure');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [period, setPeriod] = useState<'monthly' | 'yearly' | 'one_time'>('monthly');
  const [source, setSource] = useState('');
  const [notes, setNotes] = useState('');

  // Fetch costs
  const fetchCosts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`/api/secure/command-center/entities/${entityId}/costs`);
      if (!response.ok) {
        throw new Error('Failed to fetch costs');
      }
      const data = await response.json();
      setCosts(data.costs || []);
      setSummary(data.summary || { monthly_cost: 0, yearly_cost: 0, one_time_cost: 0 });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch costs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  // Add cost
  const handleAddCost = async (e: React.FormEvent) => {
    e.preventDefault();
    const amountNum = parseFloat(amount);
    if (!amountNum || amountNum <= 0) return;

    try {
      setAdding(true);
      const response = await fetch(`/api/secure/command-center/entities/${entityId}/costs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cost_type: costType,
          amount: amountNum,
          currency,
          period,
          source: source.trim() || undefined,
          notes: notes.trim() || undefined,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to add cost');
      }

      // Reset form and refresh
      setCostType('infrastructure');
      setAmount('');
      setCurrency('USD');
      setPeriod('monthly');
      setSource('');
      setNotes('');
      setShowAddForm(false);
      await fetchCosts();
      onCostChange?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add cost');
    } finally {
      setAdding(false);
    }
  };

  // Remove cost
  const handleRemoveCost = async (costId: string) => {
    if (!confirm('Remove this cost entry?')) return;

    try {
      const response = await fetch(
        `/api/secure/command-center/entities/${entityId}/costs?cost_id=${costId}`,
        { method: 'DELETE' }
      );

      if (!response.ok) {
        throw new Error('Failed to remove cost');
      }

      await fetchCosts();
      onCostChange?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove cost');
    }
  };

  if (compact) {
    return (
      <div className="flex items-center gap-4 text-sm">
        <div className="flex items-center gap-2">
          <span className="text-foreground-tertiary">Monthly:</span>
          <span className="text-foreground font-medium">
            {formatCurrency(summary.monthly_cost)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-foreground-tertiary">Yearly:</span>
          <span className="text-foreground font-medium">
            {formatCurrency(summary.yearly_cost)}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface/50 rounded-xl border border-border-muted overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-muted">
        <div>
          <h3 className="text-sm font-medium text-foreground">Cost Tracking</h3>
          {entityName && <p className="text-xs text-foreground-tertiary">for {entityName}</p>}
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="px-3 py-1.5 text-sm text-accent hover:text-accent hover:bg-accent-muted rounded-lg transition-colors"
        >
          {showAddForm ? 'Cancel' : '+ Add Cost'}
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4 p-4 bg-background/30 border-b border-border-muted">
        <div className="text-center">
          <div className="text-xs text-foreground-tertiary mb-1">Monthly</div>
          <div className="text-lg font-semibold text-green-400">
            {formatCurrency(summary.monthly_cost)}
          </div>
        </div>
        <div className="text-center">
          <div className="text-xs text-foreground-tertiary mb-1">Yearly</div>
          <div className="text-lg font-semibold text-blue-400">
            {formatCurrency(summary.yearly_cost)}
          </div>
        </div>
        <div className="text-center">
          <div className="text-xs text-foreground-tertiary mb-1">One-time</div>
          <div className="text-lg font-semibold text-purple-400">
            {formatCurrency(summary.one_time_cost)}
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mx-4 mt-4 px-3 py-2 text-sm text-red-400 bg-red-900/20 border border-red-500/30 rounded">
          {error}
        </div>
      )}

      {/* Add form */}
      {showAddForm && (
        <form onSubmit={handleAddCost} className="p-4 border-b border-border-muted bg-background/30">
          <div className="space-y-3">
            {/* Cost Type */}
            <div>
              <label className="block text-xs text-foreground-tertiary mb-1">Cost Type</label>
              <div className="grid grid-cols-5 gap-2">
                {(Object.keys(COST_TYPE_LABELS) as CostType[]).map((ct) => (
                  <button
                    key={ct}
                    type="button"
                    onClick={() => setCostType(ct)}
                    className={`p-2 text-xs rounded-lg border transition-colors ${
                      costType === ct
                        ? `${COST_TYPE_LABELS[ct].color} border-current bg-current/10`
                        : 'text-foreground-tertiary border-border-muted hover:border-border/30'
                    }`}
                  >
                    <svg className="w-4 h-4 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={COST_TYPE_LABELS[ct].icon} />
                    </svg>
                    <div className="truncate">{COST_TYPE_LABELS[ct].label}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Amount and Period */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-foreground-tertiary mb-1">
                  Amount <span className="text-red-400">*</span>
                </label>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="1000"
                  step="0.01"
                  min="0"
                  className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground placeholder:text-foreground-muted focus:border-accent focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-xs text-foreground-tertiary mb-1">Currency</label>
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground focus:border-accent focus:outline-none"
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="CAD">CAD</option>
                  <option value="AUD">AUD</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-foreground-tertiary mb-1">Period</label>
                <select
                  value={period}
                  onChange={(e) => setPeriod(e.target.value as typeof period)}
                  className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground focus:border-accent focus:outline-none"
                >
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                  <option value="one_time">One-time</option>
                </select>
              </div>
            </div>

            {/* Source */}
            <div>
              <label className="block text-xs text-foreground-tertiary mb-1">Source (optional)</label>
              <input
                type="text"
                value={source}
                onChange={(e) => setSource(e.target.value)}
                placeholder="e.g., AWS Cost Explorer, Finance System"
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground placeholder:text-foreground-muted focus:border-accent focus:outline-none"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-xs text-foreground-tertiary mb-1">Notes (optional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional context..."
                rows={2}
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground placeholder:text-foreground-muted focus:border-accent focus:outline-none resize-none"
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={adding || !amount}
              className="w-full px-4 py-2 text-sm font-medium bg-accent text-accent-foreground rounded-lg hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {adding ? 'Adding...' : 'Add Cost Entry'}
            </button>
          </div>
        </form>
      )}

      {/* Cost list */}
      <div className="max-h-96 overflow-y-auto">
        {loading ? (
          <div className="p-4 text-center text-foreground-tertiary">
            <div className="inline-block w-5 h-5 border-2 border-border border-t-orange rounded-full animate-spin" />
          </div>
        ) : costs.length === 0 ? (
          <div className="p-8 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-cream/5 mb-3">
              <svg className="w-6 h-6 text-foreground-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <p className="text-foreground-tertiary text-sm">No cost entries</p>
            <p className="text-foreground-muted text-xs mt-1">
              Track infrastructure, licensing, and labor costs
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border-muted">
            {costs.map((cost) => (
              <div key={cost.id} className="p-4 hover:bg-cream/5 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className={`p-2 rounded-lg bg-current/10 ${COST_TYPE_LABELS[cost.cost_type].color}`}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={COST_TYPE_LABELS[cost.cost_type].icon} />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg font-semibold text-foreground">
                          {formatCurrency(cost.amount, cost.currency)}
                        </span>
                        <span className="px-1.5 py-0.5 text-xs bg-cream/10 text-foreground-secondary rounded">
                          {PERIOD_LABELS[cost.period]}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-foreground-tertiary">
                        <span className={COST_TYPE_LABELS[cost.cost_type].color}>
                          {COST_TYPE_LABELS[cost.cost_type].label}
                        </span>
                        {cost.source && (
                          <>
                            <span className="text-foreground-muted">|</span>
                            <span>{cost.source}</span>
                          </>
                        )}
                      </div>
                      {cost.notes && (
                        <p className="text-xs text-foreground-tertiary mt-1">{cost.notes}</p>
                      )}
                      <div className="text-xs text-foreground-muted mt-1">
                        Added {new Date(cost.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveCost(cost.id)}
                    className="p-1.5 text-foreground-muted hover:text-red-400 hover:bg-red-900/20 rounded transition-colors"
                    title="Remove cost"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default CostTracker;
